/**
 * Service for fetching organization logos through various public APIs
 */

export interface LogoSource {
  url: string;
  source: 'clearbit' | 'brandfetch' | 'domain' | 'favicon';
  quality: 'high' | 'medium' | 'low';
}

export interface LogoSearchResult {
  logos: LogoSource[];
  organization: string;
  domain?: string;
}

/**
 * Attempts to find organization logos using multiple fallback strategies
 */
export class LogoService {
  private static cache = new Map<string, LogoSearchResult>();

  /**
   * Search for organization logos using multiple sources
   */
  static async searchLogos(
    organization: string,
    domain?: string
  ): Promise<LogoSearchResult> {
    const cacheKey = `${organization}-${domain || ''}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    const result: LogoSearchResult = {
      organization,
      domain,
      logos: [],
    };

    // Try different logo sources in order of quality preference
    const logoSources = await Promise.allSettled([
      this.getClearbitLogo(organization, domain),
      this.getDomainFavicon(domain),
      this.getGenericOrgLogo(organization),
    ]);

    // Collect successful results
    logoSources.forEach(source => {
      if (source.status === 'fulfilled' && source.value) {
        result.logos.push(source.value);
      }
    });

    // Cache the result
    this.cache.set(cacheKey, result);

    return result;
  }

  /**
   * Get logo from Clearbit Logo API (free, no auth required)
   */
  private static async getClearbitLogo(
    organization: string,
    domain?: string
  ): Promise<LogoSource | null> {
    try {
      const searchDomain = domain || this.guessDomainFromOrg(organization);

      if (!searchDomain) {
        return null;
      }

      const logoUrl = `https://logo.clearbit.com/${searchDomain}`;

      // Since we can't reliably check CORS, return the URL and let the image handle the error
      return {
        url: logoUrl,
        source: 'clearbit',
        quality: 'high',
      };
    } catch (error) {
      console.debug('Clearbit logo fetch failed:', error);
    }

    return null;
  }

  /**
   * Get favicon from domain
   */
  private static async getDomainFavicon(
    domain?: string
  ): Promise<LogoSource | null> {
    if (!domain) {
      return null;
    }

    try {
      // Use Google's favicon service as it's more reliable for CORS
      const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;

      return {
        url: faviconUrl,
        source: 'favicon',
        quality: 'medium',
      };
    } catch (error) {
      console.debug('Favicon fetch failed:', error);
    }

    return null;
  }

  /**
   * Generate a generic logo placeholder URL (using DiceBear API)
   */
  private static async getGenericOrgLogo(
    organization: string
  ): Promise<LogoSource | null> {
    try {
      // Use DiceBear's initials style for consistent organization avatars
      const initials = this.getOrganizationInitials(organization);
      const logoUrl = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(organization)}&chars=2&backgroundColor=3b82f6&fontColor=ffffff`;

      return {
        url: logoUrl,
        source: 'domain',
        quality: 'medium',
      };
    } catch (error) {
      console.debug('Generic logo generation failed:', error);
    }

    return null;
  }

  /**
   * Guess domain from organization name
   */
  private static guessDomainFromOrg(organization: string): string | null {
    const cleanOrg = organization
      .toLowerCase()
      .replace(
        /\b(inc|corp|corporation|ltd|limited|llc|group|foundation|bureau|department|agency|administration|commission|institute|organization|org)\b/g,
        ''
      )
      .replace(/[^a-z0-9\s]/g, '')
      .trim();

    if (!cleanOrg) {
      return null;
    }

    // Common mappings for known organizations
    const knownMappings: Record<string, string> = {
      'us department of commerce': 'commerce.gov',
      'us census bureau': 'census.gov',
      'world bank group': 'worldbank.org',
      'world bank': 'worldbank.org',
      'federal reserve': 'federalreserve.gov',
      'bureau of labor statistics': 'bls.gov',
      'environmental protection agency': 'epa.gov',
      'national institutes of health': 'nih.gov',
      'centers for disease control': 'cdc.gov',
      nasa: 'nasa.gov',
      noaa: 'noaa.gov',
      usgs: 'usgs.gov',
      'european central bank': 'ecb.europa.eu',
      'international monetary fund': 'imf.org',
      'united nations': 'un.org',
      oecd: 'oecd.org',
      'european union': 'europa.eu',
    };

    if (knownMappings[cleanOrg]) {
      return knownMappings[cleanOrg];
    }

    // Generate domain guess for other organizations
    const words = cleanOrg.split(' ').filter(word => word.length > 2);

    if (words.length === 0) {
      return null;
    }

    if (words.length === 1) {
      return `${words[0]}.org`;
    }

    // Take first letters of words or first word + second word
    const domainGuess =
      words.length > 2
        ? `${words.map(w => w.charAt(0)).join('')}.org`
        : `${words.join('')}.org`;

    return domainGuess;
  }

  /**
   * Get organization initials for placeholder logos
   */
  private static getOrganizationInitials(organization: string): string {
    const words = organization
      .replace(/[^a-zA-Z\s]/g, '')
      .split(' ')
      .filter(word => word.length > 0);

    if (words.length === 0) {
      return 'ORG';
    }
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }

    return words
      .slice(0, 2)
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase();
  }

  /**
   * Get the best available logo from search results
   */
  static getBestLogo(result: LogoSearchResult): LogoSource | null {
    if (result.logos.length === 0) {
      return null;
    }

    // Sort by quality preference
    const qualityOrder = { high: 3, medium: 2, low: 1 };
    const sourceOrder = { clearbit: 4, brandfetch: 3, domain: 2, favicon: 1 };

    const bestLogo = result.logos.sort((a, b) => {
      const qualityDiff = qualityOrder[b.quality] - qualityOrder[a.quality];

      if (qualityDiff !== 0) {
        return qualityDiff;
      }

      return sourceOrder[b.source] - sourceOrder[a.source];
    })[0];

    return bestLogo;
  }

  /**
   * Preload logos for a batch of organizations
   */
  static async preloadLogos(
    organizations: Array<{ organization?: string; domain?: string }>
  ): Promise<void> {
    const promises = organizations
      .filter(org => org.organization)
      .map(org => this.searchLogos(org.organization!, org.domain));

    await Promise.allSettled(promises);
  }
}
